import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import MediaQuery from 'react-responsive';
import { RemoveScroll } from 'react-remove-scroll';

import {
    closeCategoryDropdownMenu,
    closeProfileMenu,
    openCategoryDropdownMenu
} from 'tradera/state/ui/reducer';
import { BREAKPOINTS, BREAKPOINT_SIZES } from 'tradera/constants/breakpoints';
import { logger } from 'packages/logger';
import { GtmService } from 'tradera/services/google-tagmanager/google-tagmanager-service';
import {
    EnterFade,
    EnterFrom
} from 'tradera/components/transitions/transitions';
import ApplicationErrorBoundary from 'tradera/components/application-error-boundary/application-error-boundary';
import { Overlay } from 'tradera/components/scrollable-modal/overlay';
import DropDownMenuHamburger from './dropdown-menu-burger';
import { DropdownMenuContent } from './dropdown-menu-content';
import { SeoLinks } from './seo-links';
import { MODAL_STATE } from 'tradera/constants/ui';
import { classNames } from 'tradera/utils/class-names';
import styles from './dropdown-menu.module.scss';
import {
    queueBackgroundTask,
    queueUserBlockingTask
} from 'tradera/utils/scheduler';
import { useAppSelector } from 'tradera/state/hooks';
import { isButtonClickStarted } from 'tradera/utils/a11y';

const trackAnalytics = (label: string): void => {
    try {
        GtmService.trackAction('Top navigation', 'Category menu', label);
    } catch (error) {
        logger(error);
    }
};

type DropdownMenuProps = {
    className?: string;
    inSPA?: boolean;
    isLoggedIn?: boolean;
    tabIndex?: number;
};

export const DropdownMenu = ({
    className = '',
    inSPA = false,
    isLoggedIn = false,
    tabIndex
}: DropdownMenuProps) => {
    const dropdownMenu = useAppSelector(
        (state) => state.ui.categoryDropdownMenu
    );

    const expanded = dropdownMenu.open === MODAL_STATE.OPEN;
    const [shouldRender, setShouldRender] = useState(false);
    const dispatch = useDispatch();

    const onExited = useCallback(() => {
        setShouldRender(false);
    }, []);

    const renderMenu = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (isButtonClickStarted(event)) {
                setShouldRender(true);
            }
        },
        [setShouldRender]
    );

    const onClick = useCallback(() => {
        const shouldCloseMenu = expanded;
        if (shouldCloseMenu) {
            queueUserBlockingTask(() => {
                dispatch(closeCategoryDropdownMenu());
            });
            queueBackgroundTask(() => {
                trackAnalytics('Menu close');
            });
        } else {
            setShouldRender(true);
            queueUserBlockingTask(() => {
                dispatch(closeProfileMenu());
                dispatch(openCategoryDropdownMenu());
            });
            queueBackgroundTask(() => {
                trackAnalytics('Menu open');
            });
        }
    }, [dispatch, expanded]);

    const onItemClick = useCallback(() => {
        dispatch(closeCategoryDropdownMenu());
    }, [dispatch]);

    const sideMenuMediaProps = {
        maxWidth: BREAKPOINT_SIZES[BREAKPOINTS.LG].max
    };
    const dropdownMenuMediaProps = {
        minWidth: BREAKPOINT_SIZES[BREAKPOINTS.LG].max
    };

    // This function is used to get the offset of the site header caused by
    // extra banners like outage and product communication
    const getSiteHeaderOffset = (): number =>
        document.getElementById('site-header')?.getBoundingClientRect().top ??
        0;

    // This function is used to get the height difference between the 100vh
    // and the actual viewport height. This is needed for mobile browsers
    // where the address bar can be hidden or shown depending on the scroll.
    const getViewHeightCorrection = (): number => {
        const measured100vh =
            document.getElementById('measure-100vh')?.offsetHeight ?? 0;
        return measured100vh ? measured100vh - window.innerHeight : 0;
    };

    const renderChild = (className?: string) => (
        <RemoveScroll forwardProps>
            <div
                className={classNames(
                    `mt-xl-1`,
                    className,
                    styles.siteDropdown
                )}
                tabIndex={-1}
                style={{
                    ['--siteHeaderOffset' as string]: `${getSiteHeaderOffset()}px`,
                    ['--viewHeightCorrection' as string]: `${getViewHeightCorrection()}px`
                }}>
                <DropdownMenuContent
                    inSPA={inSPA}
                    isLoggedIn={isLoggedIn}
                    onClick={onItemClick}
                />
            </div>
        </RemoveScroll>
    );

    return (
        <ApplicationErrorBoundary>
            <nav className={className} role="navigation">
                <DropDownMenuHamburger
                    active={expanded}
                    className="site-dropdown__hamburger pl-0 pl-md-1"
                    onClick={onClick}
                    onPointerDown={renderMenu}
                    tabIndex={tabIndex}
                />
                {!expanded && <SeoLinks isLoggedIn={isLoggedIn} />}
                {shouldRender && (
                    <EnterFrom
                        from="left"
                        in={expanded}
                        unmountOnExit={false}
                        onExited={onExited}>
                        <MediaQuery {...sideMenuMediaProps}>
                            {renderChild('from-left-initial')}
                        </MediaQuery>
                    </EnterFrom>
                )}
                {shouldRender && (
                    <MediaQuery {...dropdownMenuMediaProps}>
                        <div className={classNames(!expanded && 'd-none')}>
                            {renderChild()}
                        </div>
                    </MediaQuery>
                )}
            </nav>
            {shouldRender && (
                <EnterFade in={expanded} unmountOnExit={false}>
                    <Overlay
                        style={{
                            zIndex: 7,
                            top: '4.5rem',
                            height: 'calc(100vh - 4.5em)'
                        }}
                        className="fade-initial"
                        closeModalCallback={() => {
                            onClick();
                        }}
                    />
                </EnterFade>
            )}
        </ApplicationErrorBoundary>
    );
};
